/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    GslbLeaderChangeMode,
    ILeaderChangeInfo,
    ISiteInfo,
} from 'generated-types';

import { LeaderChangeInfo } from 'object-types';

import {
    MessageItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

type TLeaderChangeInfoPartial = Omit<ILeaderChangeInfo, 'leader_candidates'>;

interface ILeaderChangeInfoConfig extends TLeaderChangeInfoPartial {
    leader_candidates: RepeatedMessageItem<MessageItem<ISiteInfo>>;
}

/**
 * Type of response returned by fetchGslbLeaderChangeCandidates method.
 */
export interface IEligibleLeaderCandidate {
    cluster_id: string;
    site_name: string;
}

/**
 * API url to fetch Leader Change Candidates for Leader Designation Dropdown.
 */
const CHANGE_LEADER_CANDIDATES_API_URL = '/api/gslbsiteops/changeleader?candidates';

/**
 * @description
 *
 *   Leader Change Info Message Item.
 *
 * @author Sarthak kapoor
 */
export class LeaderChangeInfoConfigItem extends MessageItem<ILeaderChangeInfoConfig> {
    protected static ngDependencies = [
        HTTP_WRAPPER_TOKEN,
    ];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: LeaderChangeInfo,
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();
    }

    /**
     * Fetch GSLB Leader change candidates.
     */
    public async fetchGslbLeaderChangeCandidates(): Promise<IEligibleLeaderCandidate[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: CHANGE_LEADER_CANDIDATES_API_URL,
                method: HttpMethod.GET,
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { eligible_leader_candidates: eligibleLeaderCandidates = [] } = response.data;

            return eligibleLeaderCandidates;
        } finally {
            this.busy = false;
        }
    }

    /** @override */
    protected modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        if (!this.config.leader_candidates.count) {
            this.config.leader_candidates.add();
        }
    }

    /** @override */
    protected modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        if (this.config.leader_change_mode === GslbLeaderChangeMode.GSLB_LC_MODE_MANUAL) {
            delete this.config.max_unsuccessful_probes;
            this.config.leader_candidates.removeAll();
        }
    }
}
